import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Arrange, Modal } from "@aviary";
import { AppleSSOButton, GoogleSSOButton } from "@shared/components";
import { useFlippers } from "@shared/hooks";
import { Size } from "@shared/types/sizes";
import { FormWrapper, ErrorBox, SignInWrapper } from "@unauthenticated/shared/components";
import { SSOErrorBox } from "@unauthenticated/shared/components/SSOErrorBox/SSOErrorBox";
import {
  StoreLogo,
  SignUpModalContainer,
  SignUpModalHeader,
  SignUpModalTitle,
  SignUpModalSubtitle,
  SignUpModalFooter,
} from "@unauthenticated/signupSignin/components";
import type { RoleAttributes } from "@unauthenticated/signupSignin/data/types";
import { l } from "@unauthenticated/signupSignin/locales/i18n";

import { OfficeSignUpBackground } from "./OfficeSignUpBackground/";
import { OfficeSignUpForm } from "./OfficeSignUpForm";
import { useLogOfficeSignUp } from "./useLogOfficeSignUp";

import * as styles from "./OfficeSignUpPage.styles";

interface Props {
  roleAttributes: RoleAttributes;
  roleType: "Clerk" | "Practitioner";
  roleId: string;
  ssoErrors?: string;
}

const OfficeSignUpPage = ({ roleAttributes, roleType, roleId, ssoErrors }: Props) => {
  const { t } = useTranslation();
  const { firstName, lastName, storeName, storeLogo, invitationToken } = roleAttributes;
  const [signUpError, setSignUpError] = useState("");
  const pageVersion = "react_with_sso";
  const signUpPage = "office_sign_up_page";
  const [isAppleSSO] = useFlippers("apple_sso");

  const { logField } = useLogOfficeSignUp({
    eventAggregateId: roleId,
    signUpPage,
    pageVersion,
  });

  return (
    <OfficeSignUpBackground roleType={roleType}>
      <Modal isOpen hideClose isSize={Size.MEDIUM} data-e2e="office-sign-up-modal">
        <SignUpModalHeader>
          <StoreLogo storeLogo={storeLogo} storeName={storeName} />
          <SignUpModalTitle title={storeName} />
          <SignUpModalSubtitle
            subtitle={t(l.officeSignUp.SignUpWelcomeMessage, {
              firstName,
              lastName,
              storeName,
            })}
          />
        </SignUpModalHeader>
        <SignUpModalContainer>
          <FormWrapper css={styles.formWrapperOverride}>
            <SSOErrorBox ssoErrors={ssoErrors} />
            {signUpError && <ErrorBox error={signUpError} />}
            <SignInWrapper>
              <Arrange isVertical alignment="center" isFullWidth>
                <GoogleSSOButton
                  isOrTextRendered={!isAppleSSO}
                  signUpPage={signUpPage}
                  pageVersion={pageVersion}
                />
                {isAppleSSO && (
                  <AppleSSOButton
                    isOrTextRendered
                    signUpPage={signUpPage}
                    pageVersion={pageVersion}
                  />
                )}
              </Arrange>

              <OfficeSignUpForm
                logField={logField}
                invitationToken={invitationToken}
                setSignUpError={setSignUpError}
              />
            </SignInWrapper>
          </FormWrapper>
        </SignUpModalContainer>
        <SignUpModalFooter />
      </Modal>
    </OfficeSignUpBackground>
  );
};

export { OfficeSignUpPage };
